import type { AuthUser } from 'features/auth';
import type { DashboardSummary } from 'features/dashboard';
import type { Device } from 'features/devices';
import type { DriverRecord } from 'features/drivers';
import type { EventRecord } from 'features/events';
import type { GeofenceRecord } from 'features/geofences';
import type { DailyReportData } from 'features/reports';
import type { AppSettingsRecord, ThresholdRule } from 'features/settings';
import type { TelemetryRecord } from 'features/telemetry';
import type { SystemUser } from './users-service';

export const demoCredentials = {
  email: 'admin@mineacs.demo',
  password: 'Demo123!'
} as const;

export const demoAuthUser: AuthUser = {
  id: 'demo-admin-001',
  name: 'MineACS Demo Admin',
  email: demoCredentials.email,
  role: 'Administrator'
};

export const demoUsers: SystemUser[] = [
  {
    id: 'demo-user-001',
    employeeId: 'DEMO-001',
    name: 'MineACS Demo Admin',
    email: demoCredentials.email,
    role: 'Administrator',
    unit: 'A',
    status: 'Active',
    lastLogin: '2026-08-05T20:00:00+08:00',
    avatarUrl: null,
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T20:00:00+08:00'
  },
  {
    id: 'demo-user-002',
    employeeId: 'EMP-102',
    name: 'Andi Pratama',
    email: 'andi.pratama@mineacs.demo',
    role: 'Supervisor',
    unit: 'A',
    status: 'Active',
    lastLogin: '2026-08-05T18:30:00+08:00',
    avatarUrl: null,
    createdAt: '2026-08-02T08:00:00+08:00',
    updatedAt: '2026-08-05T18:30:00+08:00'
  },
  {
    id: 'demo-user-003',
    employeeId: 'EMP-103',
    name: 'Budi Santoso',
    email: 'budi.santoso@mineacs.demo',
    role: 'Operator',
    unit: 'B',
    status: 'Active',
    lastLogin: null,
    avatarUrl: null,
    createdAt: '2026-08-03T08:00:00+08:00',
    updatedAt: '2026-08-03T08:00:00+08:00'
  },
  {
    id: 'demo-user-004',
    employeeId: 'EMP-104',
    name: 'Siti Rahma',
    email: 'siti.rahma@mineacs.demo',
    role: 'Viewer',
    unit: 'B',
    status: 'Inactive',
    lastLogin: '2026-08-01T10:15:00+08:00',
    avatarUrl: null,
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-04T14:00:00+08:00'
  }
];

export const demoDevices: Device[] = [
  {
    id: 'demo-device-001',
    deviceId: 'UWB-TAG-A',
    name: 'UWB Tag A',
    type: 'uwb_tag',
    unit: 'A',
    firmware: 'v2.1.0',
    installLocation: 'Dump Truck A',
    status: 'active',
    notes: 'Primary worker proximity tag',
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T19:30:00+08:00'
  },
  {
    id: 'demo-device-002',
    deviceId: 'UWB-TAG-B',
    name: 'UWB Tag B',
    type: 'uwb_tag',
    unit: 'B',
    firmware: 'v2.1.0',
    installLocation: 'Excavator B',
    status: 'active',
    notes: 'Excavator proximity tag',
    createdAt: '2026-08-01T08:10:00+08:00',
    updatedAt: '2026-08-05T19:31:00+08:00'
  },
  {
    id: 'demo-device-003',
    deviceId: 'UWB-ANCHOR-N',
    name: 'UWB Anchor North',
    type: 'uwb_anchor',
    unit: 'fixed',
    firmware: 'v1.8.4',
    installLocation: 'North pit boundary',
    status: 'active',
    notes: 'North fixed anchor',
    createdAt: '2026-08-01T09:00:00+08:00',
    updatedAt: '2026-08-05T16:00:00+08:00'
  },
  {
    id: 'demo-device-004',
    deviceId: 'UWB-ANCHOR-S',
    name: 'UWB Anchor South',
    type: 'uwb_anchor',
    unit: 'fixed',
    firmware: 'v1.8.4',
    installLocation: 'South pit access',
    status: 'maintenance',
    notes: 'Scheduled calibration',
    createdAt: '2026-08-01T09:10:00+08:00',
    updatedAt: '2026-08-05T11:00:00+08:00'
  },
  {
    id: 'demo-device-005',
    deviceId: 'GPS-DT-01',
    name: 'GPS Tracker DT-01',
    type: 'gps_tracker',
    unit: 'A',
    firmware: 'v3.0.2',
    installLocation: 'Dump Truck DT-01',
    status: 'active',
    notes: 'High accuracy GNSS module',
    createdAt: '2026-08-02T08:00:00+08:00',
    updatedAt: '2026-08-05T19:35:00+08:00'
  },
  {
    id: 'demo-device-006',
    deviceId: 'GPS-EX-01',
    name: 'GPS Tracker EX-01',
    type: 'gps_tracker',
    unit: 'B',
    firmware: 'v3.0.2',
    installLocation: 'Excavator EX-01',
    status: 'active',
    notes: 'Mounted in cabin',
    createdAt: '2026-08-02T08:20:00+08:00',
    updatedAt: '2026-08-05T19:36:00+08:00'
  },
  {
    id: 'demo-device-007',
    deviceId: 'GW-MAIN-01',
    name: 'Gateway Main Site',
    type: 'gateway',
    unit: 'fixed',
    firmware: 'v4.2.1',
    installLocation: 'Control room tower',
    status: 'active',
    notes: 'MQTT bridge gateway',
    createdAt: '2026-08-01T07:30:00+08:00',
    updatedAt: '2026-08-05T19:40:00+08:00'
  },
  {
    id: 'demo-device-008',
    deviceId: 'UWB-SPARE-01',
    name: 'UWB Spare Tag',
    type: 'uwb_tag',
    unit: 'unassigned',
    firmware: 'v2.0.8',
    installLocation: 'Warehouse',
    status: 'spare',
    notes: 'Ready for replacement',
    createdAt: '2026-08-03T10:00:00+08:00',
    updatedAt: '2026-08-03T10:00:00+08:00'
  }
];

export const demoTelemetry: TelemetryRecord[] = [
  {
    id: 'demo-tel-001',
    sender: 'A',
    unitA: 'DT-01',
    unitB: 'EX-01',
    posA: [-1.27445, 116.84835],
    posB: [-1.27415, 116.84808],
    distM: 7.4,
    rawDistM: 4.97,
    gpsFix: true,
    uwbLink: true,
    sos: false,
    sat: 12,
    hAccM: 0.8,
    speedMps: 2.3,
    headingDeg: 76,
    imu: { ax: 0.01, ay: 0.03, az: 9.76, gx: 0.02, gy: 0.01, gz: 0.04, pitch: 4, roll: 2, yaw: 76 },
    fallDetected: false,
    fallConfidence: null,
    posture: 'walking',
    seq: 1088,
    timestamp: '2026-08-05T20:00:00+08:00'
  },
  {
    id: 'demo-tel-002',
    sender: 'B',
    unitA: 'DT-01',
    unitB: 'EX-01',
    posA: [-1.27445, 116.84835],
    posB: [-1.27392, 116.84868],
    distM: 13.2,
    rawDistM: 8.86,
    gpsFix: true,
    uwbLink: true,
    sos: false,
    sat: 10,
    hAccM: 1.2,
    speedMps: 0.6,
    headingDeg: 218,
    imu: { ax: 0.02, ay: 0.02, az: 9.79, gx: 0.01, gy: 0.03, gz: 0.02, pitch: 1, roll: 3, yaw: 218 },
    fallDetected: false,
    fallConfidence: null,
    posture: 'standing',
    seq: 1089,
    timestamp: '2026-08-05T20:01:00+08:00'
  },
  {
    id: 'demo-tel-003',
    sender: 'A',
    unitA: 'DT-01',
    unitB: 'EX-01',
    posA: [-1.27474, 116.84795],
    posB: [-1.27422, 116.84804],
    distM: 4.6,
    rawDistM: 3.09,
    gpsFix: true,
    uwbLink: true,
    sos: false,
    sat: 11,
    hAccM: 0.9,
    speedMps: 1.8,
    headingDeg: 91,
    imu: { ax: 0.04, ay: 0.02, az: 9.71, gx: 0.04, gy: 0.02, gz: 0.02, pitch: 7, roll: 4, yaw: 91 },
    fallDetected: false,
    fallConfidence: null,
    posture: 'walking',
    seq: 1090,
    timestamp: '2026-08-05T20:02:00+08:00'
  }
];

export const demoEvents: EventRecord[] = [
  {
    id: 'demo-event-001',
    type: 'safe',
    severity: 'Low',
    message: 'Unit A and B operating within safe distance',
    unit: 'A',
    telemetryId: 'demo-tel-001',
    createdAt: '2026-08-05T19:50:00+08:00'
  },
  {
    id: 'demo-event-002',
    type: 'warn',
    severity: 'Medium',
    message: 'Peringatan: jarak 7.40 m',
    unit: 'A',
    telemetryId: 'demo-tel-001',
    createdAt: '2026-08-05T20:00:00+08:00'
  },
  {
    id: 'demo-event-003',
    type: 'danger',
    severity: 'High',
    message: 'Bahaya: jarak 4.60 m',
    unit: 'A',
    telemetryId: 'demo-tel-003',
    createdAt: '2026-08-05T20:02:00+08:00'
  },
  {
    id: 'demo-event-004',
    type: 'sos',
    severity: 'Emergency',
    message: 'SOS aktif dari Unit B - demo scenario',
    unit: 'B',
    telemetryId: null,
    createdAt: '2026-08-05T18:35:00+08:00'
  },
  {
    id: 'demo-event-005',
    type: 'fall',
    severity: 'Emergency',
    message: 'Fall detected Unit A - posture fallen - confidence 91%',
    unit: 'A',
    telemetryId: null,
    createdAt: '2026-08-05T17:40:00+08:00'
  }
];

export const demoDrivers: DriverRecord[] = [
  {
    id: 'demo-driver-001',
    employeeId: 'EMP-MIN-001',
    name: 'Operator Unit A',
    role: 'Hauler Driver',
    shift: 'Shift Pagi',
    unit: 'A',
    vehicle: 'Dump Truck DT-01',
    operationalMode: 'dumptruck',
    modeLocked: true,
    avatarUrl: '',
    status: 'active',
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T18:00:00+08:00'
  },
  {
    id: 'demo-driver-002',
    employeeId: 'EMP-MIN-002',
    name: 'Operator Unit B',
    role: 'Excavator Operator',
    shift: 'Shift Malam',
    unit: 'B',
    vehicle: 'Excavator EX-01',
    operationalMode: 'excavator',
    modeLocked: true,
    avatarUrl: '',
    status: 'active',
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T18:00:00+08:00'
  },
  {
    id: 'demo-driver-003',
    employeeId: 'EMP-MIN-003',
    name: 'Safety Walker',
    role: 'Field Safety',
    shift: 'Shift Pagi',
    unit: 'A',
    vehicle: 'On Foot',
    operationalMode: 'worker',
    modeLocked: false,
    avatarUrl: '',
    status: 'off_duty',
    createdAt: '2026-08-02T08:00:00+08:00',
    updatedAt: '2026-08-05T12:00:00+08:00'
  },
  {
    id: 'demo-driver-004',
    employeeId: 'EMP-MIN-004',
    name: 'Backup Operator',
    role: 'Relief Driver',
    shift: 'Standby',
    unit: 'B',
    vehicle: 'Service Vehicle',
    operationalMode: null,
    modeLocked: false,
    avatarUrl: '',
    status: 'inactive',
    createdAt: '2026-08-03T08:00:00+08:00',
    updatedAt: '2026-08-04T12:00:00+08:00'
  }
];

export const demoThresholds: ThresholdRule[] = [
  {
    id: 'demo-threshold-001',
    code: 'DIST-WARN',
    name: 'Distance Warning',
    metric: 'distance',
    severity: 'warning',
    warningValue: 10,
    dangerValue: 5,
    unit: 'm',
    active: true,
    escalation: 'Notify supervisor',
    action: 'Reduce speed and maintain safe distance',
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T08:00:00+08:00'
  },
  {
    id: 'demo-threshold-002',
    code: 'DIST-DANGER',
    name: 'Distance Danger',
    metric: 'distance',
    severity: 'danger',
    warningValue: 10,
    dangerValue: 5,
    unit: 'm',
    active: true,
    escalation: 'Trigger audible alarm',
    action: 'Stop movement immediately',
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T08:00:00+08:00'
  },
  {
    id: 'demo-threshold-003',
    code: 'GPS-ACC',
    name: 'GPS Accuracy',
    metric: 'gps_accuracy',
    severity: 'warning',
    warningValue: 2,
    dangerValue: 5,
    unit: 'm',
    active: true,
    escalation: 'Flag degraded GPS',
    action: 'Check antenna visibility',
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T08:00:00+08:00'
  },
  {
    id: 'demo-threshold-004',
    code: 'UWB-LINK',
    name: 'UWB No Link',
    metric: 'uwb_no_link',
    severity: 'emergency',
    warningValue: 1,
    dangerValue: 3,
    unit: 'frames',
    active: true,
    escalation: 'Notify control room',
    action: 'Inspect UWB tag and anchor',
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T08:00:00+08:00'
  },
  {
    id: 'demo-threshold-005',
    code: 'SPEED',
    name: 'Unit Speed',
    metric: 'speed',
    severity: 'warning',
    warningValue: 20,
    dangerValue: 30,
    unit: 'km/h',
    active: false,
    escalation: 'Supervisor review',
    action: 'Slow down in active pit',
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T08:00:00+08:00'
  },
  {
    id: 'demo-threshold-006',
    code: 'GEOFENCE',
    name: 'Restricted Zone Entry',
    metric: 'geofence',
    severity: 'danger',
    warningValue: 1,
    dangerValue: 1,
    unit: 'entry',
    active: true,
    escalation: 'Dispatch field safety',
    action: 'Exit restricted zone',
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T08:00:00+08:00'
  }
];

export const demoGeofences: GeofenceRecord[] = [
  {
    id: 'demo-geofence-001',
    name: 'Pit A Work Area',
    type: 'work_area',
    coordinates: [
      [-1.275, 116.8478],
      [-1.2738, 116.8478],
      [-1.2738, 116.8492],
      [-1.275, 116.8492]
    ],
    active: true,
    rule: 'Allow active fleet operations',
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T10:00:00+08:00'
  },
  {
    id: 'demo-geofence-002',
    name: 'Highwall Restricted Zone',
    type: 'restricted',
    coordinates: [
      [-1.2749, 116.8493],
      [-1.2742, 116.8493],
      [-1.2742, 116.8501],
      [-1.2749, 116.8501]
    ],
    active: true,
    rule: 'Entry triggers danger alert',
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T10:00:00+08:00'
  },
  {
    id: 'demo-geofence-003',
    name: 'Narrow Haul Road',
    type: 'warning',
    coordinates: [
      [-1.2752, 116.8473],
      [-1.2745, 116.8473],
      [-1.2745, 116.8477],
      [-1.2752, 116.8477]
    ],
    active: true,
    rule: 'Reduce speed and maintain radio contact',
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T10:00:00+08:00'
  },
  {
    id: 'demo-geofence-004',
    name: 'Muster Point Safe Zone',
    type: 'safe',
    coordinates: [
      [-1.2737, 116.8474],
      [-1.2732, 116.8474],
      [-1.2732, 116.848],
      [-1.2737, 116.848]
    ],
    active: true,
    rule: 'Emergency assembly point',
    createdAt: '2026-08-01T08:00:00+08:00',
    updatedAt: '2026-08-05T10:00:00+08:00'
  }
];

export const demoAppSettings: AppSettingsRecord = {
  companyName: 'Minergo Systems',
  siteName: 'MineACS Demo Site',
  updatedAt: '2026-08-05T20:00:00+08:00'
};

export const demoDashboardSummary: DashboardSummary = {
  devices: { total: 8, active: 6, maintenance: 1, spare: 1, retired: 0 },
  telemetry: {
    total: 1284,
    latest: {
      id: 'demo-tel-003',
      sender: 'A',
      distanceM: 4.6,
      gpsFix: true,
      uwbLink: true,
      sos: false,
      fallDetected: false,
      createdAt: '2026-08-05T20:02:00+08:00'
    }
  },
  events: { last24h: 12, safe: 5, warn: 3, danger: 2, sos: 1, fall: 1 },
  generatedAt: '2026-08-05T20:03:00+08:00'
};

export const demoDailyReport: DailyReportData = {
  date: '2026-08-05',
  generatedAt: '2026-08-05T20:05:00+08:00',
  summary: {
    telemetryCount: 1284,
    totalEvents: 12,
    warningCount: 3,
    dangerCount: 2,
    sosCount: 1,
    criticalDistanceCount: 2,
    noLinkCount: 1,
    gpsFixRate: 96,
    uwbLinkRate: 94,
    activeDrivers: 2,
    activeGeofences: 4
  },
  unitStats: [
    { unit: 'A', telemetryCount: 720, eventCount: 7, minDistanceM: 4.6, maxDistanceM: 22.4, avgDistanceM: 12.8 },
    { unit: 'B', telemetryCount: 564, eventCount: 5, minDistanceM: 7.4, maxDistanceM: 28.3, avgDistanceM: 15.1 }
  ],
  deviceHealth: [
    {
      unit: 'A',
      label: 'Dump Truck DT-01',
      lastSeen: '2026-08-05T20:02:00+08:00',
      gpsFix: true,
      uwbLink: true,
      sat: 12,
      hAccM: 0.8,
      seq: 1090
    },
    {
      unit: 'B',
      label: 'Excavator EX-01',
      lastSeen: '2026-08-05T20:01:00+08:00',
      gpsFix: true,
      uwbLink: true,
      sat: 10,
      hAccM: 1.2,
      seq: 1089
    }
  ],
  driverRisks: [
    {
      unit: 'A',
      name: 'Operator Unit A',
      employeeId: 'EMP-MIN-001',
      role: 'Hauler Driver',
      shift: 'Shift Pagi',
      vehicle: 'Dump Truck DT-01',
      eventCount: 7,
      highPriorityCount: 2,
      avgDistanceM: 12.8,
      riskScore: 68,
      status: 'Monitoring'
    },
    {
      unit: 'B',
      name: 'Operator Unit B',
      employeeId: 'EMP-MIN-002',
      role: 'Excavator Operator',
      shift: 'Shift Malam',
      vehicle: 'Excavator EX-01',
      eventCount: 5,
      highPriorityCount: 1,
      avgDistanceM: 15.1,
      riskScore: 42,
      status: 'Normal'
    }
  ],
  incidents: demoEvents.map((event) => ({
    id: event.id,
    type: event.type,
    message: event.message,
    unit: event.unit,
    driverName: event.unit === 'A' ? 'Operator Unit A' : event.unit === 'B' ? 'Operator Unit B' : null,
    createdAt: event.createdAt,
    priority: event.severity
  })),
  geofences: demoGeofences.map((geofence) => ({
    id: geofence.id,
    name: geofence.name,
    type: geofence.type,
    active: geofence.active,
    rule: geofence.rule
  }))
};
